import { useState } from "react";
import { apiPatch, apiPost } from "@/lib/apiClient";
import { formatCrmStatusLabel } from "@/lib/crmDisplay";

const DEAL_STAGES = ["qualification", "proposal", "negotiation", "closed_won", "closed_lost"] as const;
const DEAL_STATUSES = ["open", "won", "lost"] as const;

export type DealEditSnapshot = {
  id: string;
  name?: string | null;
  amount?: number | null;
  stage?: string | null;
  status?: string | null;
  closeDate?: string | null;
  ownerId?: string | null;
  contactIds?: string[];
  primaryContactId?: string | null;
};

type AccountContactOption = { id: string; fullName?: string | null; email?: string | null };

type CrmDealEditSectionProps = {
  dealId: string;
  accountId: string;
  initial: DealEditSnapshot;
  accountContacts: AccountContactOption[];
  onSaved: (deal: DealEditSnapshot) => void;
};

export function CrmDealEditSection({
  dealId,
  accountId,
  initial,
  accountContacts,
  onSaved,
}: CrmDealEditSectionProps) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(initial.name || "");
  const [amount, setAmount] = useState(initial.amount != null ? String(initial.amount) : "");
  const [stage, setStage] = useState(initial.stage || "qualification");
  const [status, setStatus] = useState(initial.status || "open");
  const [closeDate, setCloseDate] = useState(initial.closeDate?.slice(0, 10) || "");
  const [ownerId, setOwnerId] = useState(initial.ownerId || "");
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(
    () => new Set(initial.contactIds || []),
  );
  const [primaryContactId, setPrimaryContactId] = useState(initial.primaryContactId || "");

  const toggleContact = (contactId: string) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    const res = await apiPatch<DealEditSnapshot>(
      `/api/crm/deals/${encodeURIComponent(dealId)}?accountId=${encodeURIComponent(accountId)}`,
      {
        name: name.trim(),
        amount: amount.trim() ? Number(amount) : null,
        stage,
        status,
        closeDate: closeDate.trim() === "" ? null : closeDate.trim(),
        ownerId: ownerId.trim() === "" ? null : ownerId.trim(),
        contactIds: Array.from(selectedContactIds),
        primaryContactId: primaryContactId.trim() === "" ? null : primaryContactId.trim(),
      },
    );
    setSaving(false);
    if (!res.ok || !res.data) {
      setError(res.error || "Save failed.");
      return;
    }
    setOpen(false);
    onSaved(res.data);
  };

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-100 dark:border-indigo-400/50 dark:bg-indigo-900/30 dark:text-indigo-100"
      >
        {open ? "Cancel editing" : "Edit deal"}
      </button>
      {open ? (
        <>
          {error ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">{error}</div>
          ) : null}
          <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
            <label className="block text-sm">
              <span className="font-medium">Deal name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
              />
            </label>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-sm">
                <span className="font-medium">Amount</span>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
                />
              </label>
              <label className="text-sm">
                <span className="font-medium">Close date</span>
                <input
                  type="date"
                  value={closeDate}
                  onChange={(e) => setCloseDate(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
                />
              </label>
              <label className="text-sm">
                <span className="font-medium">Stage</span>
                <select
                  value={stage}
                  onChange={(e) => setStage(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
                >
                  {DEAL_STAGES.map((s) => (
                    <option key={s} value={s}>
                      {formatCrmStatusLabel(s)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="font-medium">Status</span>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
                >
                  {DEAL_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {formatCrmStatusLabel(s)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm md:col-span-2">
                <span className="font-medium">Owner ID</span>
                <input
                  value={ownerId}
                  onChange={(e) => setOwnerId(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
                />
              </label>
            </div>
            {accountContacts.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium">Deal contacts</p>
                <ul className="space-y-1">
                  {accountContacts.map((c) => (
                    <li key={c.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedContactIds.has(c.id)}
                        onChange={() => toggleContact(c.id)}
                      />
                      <span>{c.fullName || c.email || c.id}</span>
                    </li>
                  ))}
                </ul>
                <label className="block text-sm">
                  <span className="font-medium">Primary contact</span>
                  <select
                    value={primaryContactId}
                    onChange={(e) => setPrimaryContactId(e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
                  >
                    <option value="">None</option>
                    {accountContacts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.fullName || c.email || c.id}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            ) : null}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => void submit()}
                disabled={saving}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save deal"}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

type CreateInvoiceFromDealButtonProps = {
  dealId: string;
  accountId: string;
  dealStage?: string | null;
};

export function CreateInvoiceFromDealButton({ dealId, accountId, dealStage }: CreateInvoiceFromDealButtonProps) {
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const won = dealStage === "closed_won" || dealStage === "won";

  if (!won) return null;

  const onCreate = async () => {
    if (!window.confirm("Create a Finance invoice from this won deal?")) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    const res = await apiPost<{ invoice?: { number?: string }; created?: boolean }>(
      `/api/crm/deals/${encodeURIComponent(dealId)}/create-invoice?accountId=${encodeURIComponent(accountId)}`,
      {},
    );
    setSaving(false);
    if (!res.ok) {
      setError(res.error || "Could not create invoice.");
      return;
    }
    const num = res.data?.invoice?.number;
    setMessage(res.data?.created ? `Created invoice ${num || ""}`.trim() : `Invoice already exists (${num || "linked"}).`);
  };

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => void onCreate()}
        disabled={saving}
        className="rounded-md border border-emerald-400 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-500/40 dark:bg-emerald-950/40 dark:text-emerald-100"
      >
        {saving ? "Creating…" : "Create invoice from deal"}
      </button>
      {message ? <p className="text-xs text-emerald-700 dark:text-emerald-300">{message}</p> : null}
      {error ? <p className="text-xs text-red-700 dark:text-red-300">{error}</p> : null}
    </div>
  );
}
